/**
 * The two decisions the intake screen makes about a held document, kept out of
 * the page so they are testable without React (the pattern supplier-prefill.js
 * established in 25.2).
 *
 * Phase 25.4 (PRD 13.7, 13.8).
 */

// Mirrors MAX_READ_ATTEMPTS in backend/services/invoice_intake.py. A mail the
// poller could not read stays in the mailbox Inbox and is retried, but it
// stops after this many attempts — otherwise an Inbox that never empties would
// destroy the "an empty Inbox means nothing needs attention" guarantee.
//
// There is deliberately NO "abandoned" status in the database: the row stays
// `error`, because it IS still an error and nothing about it changed except
// that we stopped retrying. The distinction is derived here, for display only.
export const MAX_READ_ATTEMPTS = 3;

/**
 * True when the poller has given up re-reading this mail.
 *
 * The user needs to know the difference: an `error` row will be retried on the
 * next poll and may yet resolve itself, while a given-up one never will and
 * the mail is sitting in the mailbox waiting for a person. Both are visible
 * either way — Principle 2 — but only one of them needs a human today.
 */
export function isAbandoned(doc) {
    return doc?.status === 'error' && (doc?.attempts || 0) >= MAX_READ_ATTEMPTS;
}

/**
 * The payload handed to the supplier-invoice dialog when a held document is
 * confirmed.
 *
 * Always a supplier INVOICE (KD-093): Supplier Invoices is for payables
 * only. A mailed receipt belongs in the cardholder's expense report and is
 * dismissed on the intake screen rather than confirmed.
 *
 * `fields` is passed through untouched. What may actually be written onto the
 * form is decided by buildPrefillPatch in supplier-prefill.js — one rule for
 * every document, whether it was picked from disk or arrived by mail.
 */
export function buildIntakeHandoff(doc) {
    if (!doc) return null;
    return {
        id: doc.id,
        fields: doc.fields || {},
        // The file is ALREADY stored, at a path inside this tenant's document
        // directory — there is nothing to upload, so the path travels and the
        // create call carries it straight through.
        filePath: (doc.file_paths || [])[0] || null,
        fileName: (doc.file_names || [])[0] || null,
        supplierId: doc.matched_supplier_id || null,
        subject: doc.subject || null,
    };
}
